import BaseDialog from "./BaseDialog";
import { itx } from "../translationItx/itx";
import { useEffect, useState } from "react";
import DeveloperAddLicenseSelectionDialog from "./DeveloperAddLicenseSelectionDialog";
import { isDevelopmentEnvironment } from "../estada-config";

/**
 * Dialog zum Erwerb von Lizenzen im Shop.
 * Zeigt die aktuell verwendeten Lizenzen, die Geräte-Nutzung und einen Link zum Shop an.
 * Wird angezeigt, wenn Benutzer das Gerätelimit überschreiten oder Premium-Features nutzen möchten.
 */
export default function ToShopLicensesDialog(props: {}) {
  const {} = props;
  const [refreshCounter, setRefreshCounter] = useState(0);

  // if they dont have any licences til now the devices will be new set
  if (state.projectLicense.licenses === undefined || state.projectLicense.licenses.length === 0) {
    state.projectLicense.deviceIds = undefined;
  }

  function onLicensesAdded() {
    setRefreshCounter((prev) => prev + 1);
  }

  function DeveloperLicenseButton(props: {}) {
    const {} = props;
    if (!isDevelopmentEnvironment()) {
      return null;
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <button
          className="button circle"
          data-tooltip-id="tv-tooltip"
          data-tooltip-content={itx("ToShopLicensesDialog.developerAddLicens", "Lizenz hinzufügen")}
          data-tooltip-place="right"
          onClick={() => {
            state.dialogManager.pushDialog(
              "to-shop-licenses-developer-selection", // no itx
              <DeveloperAddLicenseSelectionDialog onLicensesAdded={onLicensesAdded} />,
            );
          }}
        >
          <i className="las la-plus-circle" />
        </button>
      </div>
    );
  }

  /**
   * Zeigt eine Liste aller gekauften Lizenzen des Projekts an.
   * Für jede Lizenz werden Name, Kaufdatum und Anzahl der Geräte dargestellt.
   * Zeigt ein leeres Feld mit Hinweistext an, wenn keine Lizenzen vorhanden sind.
   */
  function UsedLicensesDisplay(props: {}) {
    const [licenses, setLicenses] = useState(state.projectLicense.licenses || []);

    if (!licenses || licenses.length === 0) {
      state.projectMetadata.projectLicence?.invoiceIds.forEach((invoiceId) => {
        licenses.push({
          type: invoiceId,
          purchaseDate: "",
          invoiceId: invoiceId,
          itemId: invoiceId == "dummy" ? "Einzel-Lizenz" : invoiceId,
        });
      });
    }

    /**
     * Extrahiert Anzeigeinformationen aus dem Lizenztyp-String.
     * @param type - Der Lizenztyp (z.B. "10-Devices-EUR")
     * @returns Objekt mit Name, Geräteanzahl und Icon für die Lizenz
     */
    const getLicenseDisplayInfo = (type: string) => {
      const deviceNumberMatch = type.match(/\d+/);
      let deviceNumber = deviceNumberMatch ? parseInt(deviceNumberMatch[0]) : 0;

      let name2 = type.split("-");
      // last part is every time the currency
      name2.pop();

      let name = name2.join(" ");
      if (type.toLowerCase().includes("dummy")) {
        deviceNumber = 1000;
        name = itx("ToShopLicensesDialog.dummyLicense", "Einzellizenz");
      }

      return {
        name: name,
        devices: deviceNumber,
        icon: deviceNumber > 0 ? "📦" : "📄", // no itx
      };
    };

    function DeleteLicenseButton(props: { itemId: string }) {
      const { itemId } = props;
      return (
        <button
          className="button circle"
          onClick={() => {
            state.projectLicense.removeLicense(itemId);
            setLicenses(state.projectLicense.licenses || []);
          }}
          data-tooltip-id="tv-tooltip"
          data-tooltip-content={itx(
            "ToShopLicensesDialog.deleteEstadaAddedLicence",
            "Lizenz (erstellt von Estada) entfernen",
          )}
          data-tooltip-place="right"
        >
          <i className="las la-trash" />
        </button>
      );
    }

    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          border: "2px solid var(--primary-color)",
          borderRadius: "8px",
          padding: "16px",
          backgroundColor: "#ffffff",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div
          style={{
            fontSize: "16px",
            fontWeight: "700",
            color: "#2c3e50",
            marginBottom: "16px",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {itx("ToShopLicensesDialog.yourLicenses", "Deine Lizenzen")}
          <DeveloperLicenseButton />
        </div>

        <div
          style={{
            maxHeight: "200px",
            overflowY: "auto",
            borderRadius: "8px",
            padding: "4px",
            backgroundColor: "#f8f9fa",
            border: "1px solid #dee2e6",
          }}
        >
          {!licenses || licenses.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: "32px 16px",
                minHeight: "120px",
              }}
            >
              <div
                style={{
                  fontSize: "40px",
                  marginBottom: "12px",
                  opacity: 0.3,
                }}
              >
                📄
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#6c757d",
                  textAlign: "center",
                  fontWeight: "500",
                }}
              >
                {itx("ToShopLicensesDialog.noLicensesYet", "Noch keine Lizenzen vorhanden")}
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {licenses.map((license, index) => {
                const info = getLicenseDisplayInfo(license.type);
                return (
                  <div
                    key={index}
                    style={{
                      background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      border: "2px solid #dee2e6",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <span style={{ fontSize: "20px" }}>{info.icon}</span>
                      <div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#2c3e50",
                          }}
                        >
                          {info.name}
                        </div>
                        {info.name != itx("ToShopLicensesDialog.dummyLicense", "Einzellizenz") && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6c757d",
                              marginTop: "2px",
                            }}
                          >
                            {itx("ToShopLicensesDialog.purchaseDate", "Gekauft am:")}{" "}
                            {new Date(license.purchaseDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "var(--primary-color)",
                      }}
                    >
                      {info.devices > 0 && `${info.devices} ${itx("ToShopLicensesDialog.devices", "Geräte")}`}
                    </div>
                    {isDevelopmentEnvironment() && license.itemId.includes("fromEstada") && (
                      <DeleteLicenseButton itemId={license.itemId} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  /**
   * Zeigt die Geräte-Nutzung mit Fortschrittsbalken und Status-Badge an.
   * Visualisiert die Anzahl der verwendeten vs. lizenzierten Geräte.
   * Zeigt Warnung bei Limit-Überschreitung oder Abo-Status an.
   */
  function DeviceCounterDisplay(props: {}) {
    const {} = props;
    const [maxDevices, setMaxDevices] = useState(state.projectLicense.licensedDevices);
    const [usedDevices, setUsedDevices] = useState(state.projectLicense.deviceCount);
    const percentage = maxDevices > 0 ? (usedDevices / maxDevices) * 100 : 100;
    const isOverLimit = usedDevices > maxDevices;

    useEffect(() => {
      state.projectLicense.checkAndSetDevices();
      // the usedDevices could be changed by checkAndSetDevices
      setUsedDevices(state.projectLicense.deviceCount);
      setMaxDevices(state.projectLicense.licensedDevices);
    }, []);
    /**
     * Zeigt eine farbige Status-Badge für Abo oder Limit-Überschreitung an.
     * Grün: Aktives Abo mit unbegrenzten Geräten
     * Rot: Gerätelimit überschritten, Shop-Hinweis
     * @returns null wenn weder Abo noch Limit-Überschreitung vorliegt
     */
    function LicenseStatusBadge(props: {}) {
      const {} = props;
      const hasAbo =
        state.loggedInUser.abo && state.loggedInUser.abo != "no" && !state.loggedInUser.abo.includes("dummy");

      function getAboName(): string {
        let res = state.loggedInUser?.abo || "";
        if (res.indexOf("-") != -1) {
          res = res.substring(0, res.indexOf("-"));
        }
        if (res.includes("dummy")) {
          res = "";
        }
        return res;
      }

      if (!hasAbo && !isOverLimit) {
        return null;
      }

      const isSuccess = hasAbo && !isOverLimit;
      const isError = isOverLimit;

      return (
        <div
          style={{
            marginTop: isSuccess ? "8px" : "16px",
            padding: "12px 16px",
            background: isSuccess ? "linear-gradient(135deg, #27ae60 0%, #229954 100%)" : "#ffffff", // no itx
            borderRadius: "8px", // no itx
            border: isSuccess ? "2px solid #1e8449" : "2px solid #e74c3c",
            boxShadow: isSuccess ? "0 4px 12px rgba(39, 174, 96, 0.4)" : "none", // no itx
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "700",
              color: isSuccess ? "#ffffff" : "#e74c3c",
              textAlign: "center",
              marginBottom: isSuccess ? "4px" : "0px",
            }}
          >
            {isSuccess
              ? itx("ToShopLicensesDialog.activeSubscription", "Aktives Abonnement") + " " + getAboName().toUpperCase()
              : itx("ToShopLicensesDialog.limitExceeded", "Gerätelimit überschritten").toUpperCase()}
            {isError && (
              <>
                <br />
                {itx("ToShopLicensesDialog.limitExceededText", "Weitere Lizenzen jetzt im Shop erwerben!")}
              </>
            )}
          </div>
          {isSuccess && (
            <div
              style={{
                fontSize: "12px",
                color: "#e8f5e9",
                textAlign: "center",
              }}
            >
              {itx("ToShopLicensesDialog.unlimitedDevices", "Unbegrenzte Geräteanzahl")}
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: "8px",
          padding: "20px",
          border: "2px solid var(--primary-color)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          backgroundColor: "#ffffff",
          gap: "16px",
        }}
      >
        <div
          style={{
            fontSize: "16px",
            color: "#2c3e50",
            marginBottom: "8px",
            fontWeight: "700",
            textAlign: "center",
          }}
        >
          {itx("ToShopLicensesDialog.deviceCount", "Gerätenutzung")}
        </div>
        <div
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            color: isOverLimit ? "#e74c3c" : "#2c3e50",
            marginBottom: "12px",
            textAlign: "center",
          }}
        >
          {itx("ToShopLicensesDialog.T1FromT2UsedDevices", `T1 von T2 lizenzierten Geräten verwendet`, {
            T1: usedDevices,
            T2: maxDevices,
          })}
        </div>

        <div
          style={{
            width: "100%",
            height: "36px",
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            overflow: "hidden",
            position: "relative",
            marginTop: "8px",
            border: "2px solid #555555",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "#E57E04",
              position: "absolute",
              clipPath: `inset(0 ${100 - Math.min(percentage, 100)}% 0 0)`,
              transition: "clip-path 0.3s ease",
              borderRadius: "6px",
              top: 0,
              left: 0,
            }}
          />
        </div>

        <LicenseStatusBadge />
      </div>
    );
  }

  /**
   * Zeigt einen Call-to-Action Button mit Text zum Shop-Besuch an.
   * Erklärt die Vorteile von Lizenzen und Abonnements.
   */
  function ShopButton(props: {}) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          gap: "12px",
          padding: "20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "2px solid #e9ecef",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            lineHeight: "1.6",
            color: "#495057",
            textAlign: "center",
            fontWeight: "500",
          }}
        >
          {itx(
            "ToShopLicensesDialog.text",
            "Erwerbe weitere Lizenzen und nutze zusätzliche Geräte\noder erwerbe ein Abo für unbegrenzte Geräteanzahl.",
          )
            .split("\n")
            .map((line, index) => (
              <span key={index}>
                {line}
                <br />
              </span>
            ))}
        </div>
        <button
          className="button"
          style={{
            background: "#E57E04",
            color: "#ffffff",
            fontSize: "16px",
            fontWeight: "700",
            cursor: "pointer",
            letterSpacing: "1.5px",
            alignSelf: "center",
            minWidth: "220px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            padding: "24px 24px",
            borderRadius: "4px",
          }}
          onClick={(ev) => {
            state.dialogManager.closeDialog();
            state.navigate("/shop");
          }}
        >
          {itx("ToShopLicensesDialog.shopButton", "Lizenzen vergleichen")}
          <i className="las la-arrow-right" style={{ fontSize: "18px", fontWeight: "900" }}></i>
        </button>
      </div>
    );
  }

  return (
    <BaseDialog title={itx("ToShopLicensesDialog.title", "Deine Lizenzen")} noOkButton={true} noAbortButton={true}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          minWidth: "900px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "24px",
            alignItems: "stretch",
          }}
        >
          <div style={{ flex: 1 }}>
            <UsedLicensesDisplay key={`licenses-${refreshCounter}`} />
          </div>
          <div style={{ flex: 1 }}>
            <DeviceCounterDisplay key={`devices-${refreshCounter}`} />
          </div>
        </div>
        <ShopButton />
      </div>
    </BaseDialog>
  );
}
